import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AppNavbar from '../components/AppNavbar';
import ClassificationBanner from '../components/ClassificationBanner';
import Header from '../components/Header';
import RequestClassificationField, { classificationIsCui } from '../components/RequestClassificationField';
import SchemaBodyFields from '../components/SchemaBodyFields';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import axiosInstance from '../utils/axiosInstance';
import {
  fieldName,
  fieldType,
  fileList,
  normalizeValuesForSubmit,
} from '../utils/requestContract';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

function businessUnitLabel(unit) {
  const parent = unit?.parent_partner?.display_name || unit?.parent_partner?.partner_code;
  const unitName = unit?.display_name || unit?.business_unit_code || unit?.business_unit_id;
  return parent ? `${parent} / ${unitName}` : unitName;
}

function sectionTitle(text) {
  return (
    <Typography variant="h6" sx={{ fontWeight: 800 }}>
      {text}
    </Typography>
  );
}

export default function CapabilityRequestPage(props) {
  const { capabilityDefinitionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [contract, setContract] = useState(null);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [form, setForm] = useState({
    requesting_business_unit_id: '',
    classification: '',
    title: '',
    description: '',
  });
  const [sharedValues, setSharedValues] = useState({});
  const [capabilityValues, setCapabilityValues] = useState({});
  const [fileValues, setFileValues] = useState({});
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    let isMounted = true;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const [schemaResponse, businessUnitResponse] = await Promise.all([
          axiosInstance.get(`/capabilities/${encodeURIComponent(capabilityDefinitionId)}/request-schema`),
          axiosInstance.get('/requests/business-units'),
        ]);
        const nextContract = schemaResponse.data || {};
        const nextBusinessUnits = Array.isArray(businessUnitResponse.data?.business_units)
          ? businessUnitResponse.data.business_units
          : [];

        if (!isMounted) {
          return;
        }

        setContract(nextContract);
        setBusinessUnits(nextBusinessUnits);
        setForm((current) => ({
          ...current,
          title: current.title || `Request ${nextContract.capability_name || 'Capability'}`,
          requesting_business_unit_id: current.requesting_business_unit_id || (nextBusinessUnits.length === 1 ? nextBusinessUnits[0].business_unit_id : ''),
        }));
      } catch (err) {
        if (!isMounted) {
          return;
        }
        setError(err?.response?.data?.message || err?.message || 'Unable to load request form.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, [capabilityDefinitionId]);

  const effectiveSchema = contract?.effective_request_schema || {};
  const sharedFields = useMemo(() => effectiveSchema.shared_fields || [], [effectiveSchema.shared_fields]);
  const additionalFields = useMemo(() => effectiveSchema.additional_fields || [], [effectiveSchema.additional_fields]);

  const updateValue = (setter) => (field, value) => {
    const name = fieldName(field);
    setter((current) => ({ ...current, [name]: value }));
  };

  const updateFileValue = (field, value) => {
    const name = fieldName(field);
    setFileValues((current) => ({ ...current, [name]: value }));
  };

  const uploadFilesForFields = async (fields, source) => {
    const uploaded = {};
    for (const field of fields) {
      if (fieldType(field) !== 'file') {
        continue;
      }
      const name = fieldName(field);
      const files = fileList(fileValues[name]);
      if (!files.length) {
        continue;
      }
      const artifactIds = [];
      for (const file of files) {
        const uploadResponse = await axiosInstance.post(
          `/capabilities/${encodeURIComponent(capabilityDefinitionId)}/request/upload`,
          {
            requesting_business_unit_id: form.requesting_business_unit_id,
            is_cui: classificationIsCui(form.classification),
            field_key: name,
            source,
            filename: file.name,
            content_type: file.type || 'application/octet-stream',
            size_bytes: file.size,
          },
        );
        const uploadUrl = uploadResponse.data?.upload_url;
        if (!uploadUrl) {
          throw new Error('Upload URL was not returned.');
        }
        const s3Response = await fetch(uploadUrl, {
          method: uploadResponse.data?.method || 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!s3Response.ok) {
          throw new Error(`Upload failed for ${file.name}.`);
        }
        artifactIds.push(uploadResponse.data?.artifact_id || uploadResponse.data?.file_id);
      }
      uploaded[name] = field.multiple ? artifactIds : artifactIds[0];
    }
    return uploaded;
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!form.requesting_business_unit_id) {
      nextErrors.requesting_business_unit_id = 'Requesting Business Unit is required.';
    }
    if (!form.classification) {
      nextErrors.classification = 'Content Classification is required.';
    }
    if (!form.title.trim()) {
      nextErrors.title = 'Request Title is required.';
    }
    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submitRequest = async () => {
    if (!validateForm()) {
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const sharedUploads = await uploadFilesForFields(sharedFields, 'shared');
      const capabilityUploads = await uploadFilesForFields(additionalFields, 'capability');
      const payload = {
        requesting_business_unit_id: form.requesting_business_unit_id,
        is_cui: classificationIsCui(form.classification),
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        shared_request_values: normalizeValuesForSubmit(sharedFields, sharedValues, sharedUploads),
        capability_request_values: normalizeValuesForSubmit(additionalFields, capabilityValues, capabilityUploads),
      };
      const response = await axiosInstance.post(
        `/capabilities/${encodeURIComponent(capabilityDefinitionId)}/request`,
        payload,
      );
      const requestCapabilityId = response.data?.request_capability_id;
      if (!requestCapabilityId) {
        throw new Error('Request was submitted, but the request id was missing.');
      }
      navigate(`/requests/direct/${requestCapabilityId}`);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to submit request.');
    } finally {
      setSubmitting(false);
    }
  };

  const provider = contract?.provider_business_unit || {};
  const providerName = businessUnitLabel(provider);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'Requests', href: '/requests' },
              { label: contract?.capability_name || 'Request Capability' },
            ]}
          />

          {loading ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={24} />
              <Typography>Loading request form...</Typography>
            </Stack>
          ) : error && !contract ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <Stack spacing={3} sx={{ maxWidth: 980 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    Request {contract?.capability_name || 'Capability'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {providerName}
                  </Typography>
                </Box>
                <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate(-1)}>
                  Back
                </Button>
              </Stack>

              {form.classification ? (
                <ClassificationBanner isCui={classificationIsCui(form.classification)} />
              ) : null}

              {error ? <Alert severity="error">{error}</Alert> : null}

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  {sectionTitle('Shared Need Information')}
                  <SchemaBodyFields
                    fields={sharedFields}
                    values={sharedValues}
                    fileValues={fileValues}
                    disabled={submitting}
                    onChange={updateValue(setSharedValues)}
                    onFileChange={updateFileValue}
                  />
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  {sectionTitle(`Provider-Specific Information${provider?.display_name ? ` Requested by ${provider.display_name}` : ''}`)}
                  {additionalFields.length ? (
                    <SchemaBodyFields
                      fields={additionalFields}
                      values={capabilityValues}
                      fileValues={fileValues}
                      disabled={submitting}
                      onChange={updateValue(setCapabilityValues)}
                      onFileChange={updateFileValue}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">No provider-specific inputs.</Typography>
                  )}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  {sectionTitle('Request Details')}
                  <TextField
                    select
                    label="Requesting Business Unit"
                    value={form.requesting_business_unit_id}
                    onChange={(event) => setForm((current) => ({ ...current, requesting_business_unit_id: event.target.value }))}
                    required
                    fullWidth
                    disabled={submitting || businessUnits.length === 1}
                    error={Boolean(formErrors.requesting_business_unit_id)}
                    helperText={formErrors.requesting_business_unit_id || ' '}
                  >
                    {businessUnits.map((unit) => (
                      <MenuItem key={unit.business_unit_id} value={unit.business_unit_id}>
                        {businessUnitLabel(unit)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <RequestClassificationField
                    value={form.classification}
                    onChange={(event) => setForm((current) => ({ ...current, classification: event.target.value }))}
                    disabled={submitting}
                    error={Boolean(formErrors.classification)}
                    helperText={formErrors.classification || 'Select whether this request contains Controlled Unclassified Information.'}
                  />
                  <TextField
                    label="Request Title"
                    value={form.title}
                    onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                    required
                    fullWidth
                    disabled={submitting}
                    error={Boolean(formErrors.title)}
                    helperText={formErrors.title || ' '}
                  />
                  <TextField
                    label="Additional Notes"
                    value={form.description}
                    onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                    multiline
                    minRows={4}
                    fullWidth
                    disabled={submitting}
                  />
                  <Divider />
                  <Stack direction="row" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      startIcon={<SendRoundedIcon />}
                      disabled={submitting}
                      onClick={submitRequest}
                    >
                      {submitting ? 'Submitting...' : 'Submit Request'}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            </Stack>
          )}
        </Box>
      </Box>
    </AppTheme>
  );
}
